/* eslint-disable lingui/no-unlocalized-strings -- U02: legacy wire values and synthetic backend data; UI uses Lingui. */
import { billingApi, downloadBlob, requestBlob } from '@/shared/http';

import {
  bankSlipResponseSchema,
  type BankSlipResponse,
  type OtherInformation,
  type SettlementBody,
} from './bank-slip.wire';

import { bankSlipWireDate } from './bank-slip.presentation';

const path = (barCode: string, action: string) =>
  `/BankSlip/${encodeURIComponent(barCode)}/${action}`;

export async function getBankSlip(id: string, signal?: AbortSignal) {
  const data = await billingApi.get(
    `/BankSlip/${encodeURIComponent(id)}`,
    signal ? { signal } : {},
  );
  const parsed = bankSlipResponseSchema.safeParse(data);
  if (parsed.success) return parsed.data;
  if (!import.meta.env.PROD) console.warn('BankSlip response outside the documented contract');
  return data as BankSlipResponse;
}
export async function downloadBankSlip(input: {
  barCode: string;
  walletCode?: string;
  useUpdated?: boolean;
}) {
  const file = await requestBlob(
    billingApi,
    {
      method: 'GET',
      url: path(input.barCode, 'DownloadPdf'),
      params: {
        walletCode: input.walletCode,
        ...(input.useUpdated ? { BankSlipUpdate: true } : {}),
      },
    },
    { fallbackFileName: 'boleto.pdf' },
  );
  return file;
}
export async function downloadBankSlipReceipt(id: string) {
  return requestBlob(
    billingApi,
    {
      method: 'POST',
      url: `/FileConfirmation/Transaction/${encodeURIComponent(id)}/DownloadReceipt?IsBilling=false`,
      data: {},
    },
    { fallbackFileName: 'recibo.pdf' },
  );
}
export async function saveBankSlipDownload(input: Parameters<typeof downloadBankSlip>[0]) {
  const file = await downloadBankSlip(input);
  downloadBlob(file.blob, file.fileName);
}
export const bankSlipOperations = {
  updateDueDate: ({ barCode, dueDate }: { barCode: string; dueDate: string }) =>
    billingApi.post(path(barCode, 'UpdateDueDate'), { dueDate: bankSlipWireDate(dueDate) }),
  protest: ({ barCode }: { barCode: string }) =>
    billingApi.post(path(barCode, 'DebtCollection/Submit'), {}),
  cancel: ({ barCode, keepBillingOpen }: { barCode: string; keepBillingOpen: boolean }) =>
    billingApi.post(path(barCode, 'Cancel'), { keepBillingOpen }),
  settlement: ({ barCode, body }: { barCode: string; body: SettlementBody }) =>
    billingApi.post(path(barCode, 'SettlementSTR'), {
      ...body,
      paymentDate: bankSlipWireDate(body.paymentDate),
    }),
  insertDeduction: ({ barCode, deduction }: { barCode: string; deduction: string }) =>
    billingApi.post(path(barCode, 'UpdateDeduction'), { deduction }),
  transfer: ({ barCode, toWalletCode }: { barCode: string; toWalletCode: string }) =>
    billingApi.post(path(barCode, 'Transfer'), { toWalletCode }),
  transferBatch: (body: { toWalletCode: string | number; barCodes: string[] }) =>
    billingApi.post('/BankSlip/TransferBatch', body),
  downloadLongBatch: (body: { billingIds: string[]; email: string }) =>
    billingApi.post('/BankSlip/DownloadLongBatch', body),
  cancelProtest: ({
    barCode,
    walletCode,
    instructionTransactionTypeValue,
  }: {
    barCode: string;
    walletCode: number;
    instructionTransactionTypeValue: string;
  }) =>
    billingApi.post(path(barCode, 'DebtCollection/Cancel'), {
      instructionTransactionTypeValue,
      walletCode,
    }),
  updateOtherInformation: ({ barCode, body }: { barCode: string; body: OtherInformation }) =>
    billingApi.post(path(barCode, 'UpdateOtherInformation'), body),
  updateAmount: ({ barCode, amount }: { barCode: string; amount: string }) =>
    billingApi.post(path(barCode, 'UpdateAmount'), { amount }),
  registerAgain: ({ barCode }: { barCode: string }) =>
    billingApi.post(path(barCode, 'RegisterCanceled'), {}),
  cancelDeduction: ({ barCode }: { barCode: string }) =>
    billingApi.post(path(barCode, 'CancelDeduction'), {}),
};
export async function lookupBankSlipZipCode(zipCode: string) {
  return billingApi.post<{ address: string }>(`/ZipCode/${encodeURIComponent(zipCode)}`, {});
}
